import type {
  PublicRecord,
  RecordType,
  VerificationStatus,
  VisibilityType,
} from '@/types/publicRecords';

/** Ertzyx trust ladder (levels 0–8). */
export type TrustLevel =
  | 'draft'
  | 'self_recorded'
  | 'evidence_added'
  | 'witness_confirmed'
  | 'institution_reviewed'
  | 'verified_proof'
  | 'certificate_eligible'
  | 'certificate_issued'
  | 'archived';

/** @deprecated Use TrustLevel — kept for gradual migration */
export type TrustLadderStatus = TrustLevel;

export type RecordTrustIntent =
  | 'self_recorded'
  | 'add_evidence'
  | 'request_witness'
  | 'request_institution'
  | 'request_certificate';

export type ClaimType =
  | 'personal_memory'
  | 'community_story'
  | 'achievement'
  | 'contribution'
  | 'heritage'
  | 'degree'
  | 'employment'
  | 'license'
  | 'award'
  | 'volunteer'
  | 'program_completion'
  | 'institutional_recognition'
  | 'public_service'
  | 'other';

const PUBLIC_VISIBILITIES = new Set<VisibilityType>([
  'community_public',
  'institution_public',
  'verified_public_proof',
  'community_summary',
  'restricted_proof',
  'share_link',
  'external_public',
]);

const CERTIFICATE_ELIGIBLE_TYPES = new Set<RecordType>([
  'individual_achievement',
  'institution_verified_record',
  'institution_certificate',
  'heritage_archive',
  'cultural_collection',
  'public_proof_card',
]);

/** No external verification required — preserve as self-recorded / personal. */
export const NO_VERIFICATION_REQUIRED_TYPES = new Set<RecordType>([
  'individual_memory',
  'individual_legacy',
]);

/** High-risk claims — institution review required before verified proof or certificate. */
export const HIGH_RISK_CLAIM_TYPES = new Set<ClaimType>([
  'degree',
  'employment',
  'license',
  'award',
  'volunteer',
  'program_completion',
  'institutional_recognition',
]);

const WITNESS_OR_HIGHER: VerificationStatus[] = [
  'Witness Confirmed',
  'Institution Verified',
  'Gold Verified',
];

const INSTITUTION_REVIEWED: VerificationStatus[] = ['Institution Verified', 'Gold Verified'];

export const TRUST_LEVEL_LABELS: Record<
  TrustLevel,
  { label: string; color: string; bg: string; description: string; level: number }
> = {
  draft: {
    level: 0,
    label: 'Draft',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.12)',
    description: 'Private work in progress.',
  },
  self_recorded: {
    level: 1,
    label: 'Self-Recorded',
    color: '#94a3b8',
    bg: 'rgba(148,163,184,0.14)',
    description: 'The owner preserved the record. No external proof required.',
  },
  evidence_added: {
    level: 2,
    label: 'Evidence Added',
    color: '#60a5fa',
    bg: 'rgba(96,165,250,0.12)',
    description: 'Photos, files, links, dates, locations, or supporting context attached.',
  },
  witness_confirmed: {
    level: 3,
    label: 'Witness Confirmed',
    color: '#a78bfa',
    bg: 'rgba(167,139,250,0.12)',
    description: 'A peer, family member, teammate, mentor, or attendee confirmed the record.',
  },
  institution_reviewed: {
    level: 4,
    label: 'Institution Reviewed',
    color: '#34d399',
    bg: 'rgba(52,211,153,0.12)',
    description: 'An authorized institution or reviewer confirmed the record.',
  },
  verified_proof: {
    level: 5,
    label: 'Verified Proof',
    color: '#22d3ee',
    bg: 'rgba(34,211,238,0.12)',
    description: 'Enough structured evidence and review context for a verified proof summary.',
  },
  certificate_eligible: {
    level: 6,
    label: 'Certificate Eligible',
    color: '#e9a84c',
    bg: 'rgba(233,168,76,0.14)',
    description: 'Meets requirements for certificate issuance. Private until explicitly shared.',
  },
  certificate_issued: {
    level: 7,
    label: 'Certificate Issued',
    color: '#d97706',
    bg: 'rgba(217,119,6,0.16)',
    description: 'Certificate issued. Private by default; shareable only with explicit consent.',
  },
  archived: {
    level: -1,
    label: 'Archived',
    color: '#8b9cb0',
    bg: 'rgba(139,156,176,0.12)',
    description: 'Record archived and not actively shared.',
  },
};

/**
 * Keys into the `trust_ladder` i18n namespace for a trust level's localized label/description.
 * This file has no React context to call useTranslation() itself, so callers pass these into
 * their own t() -- with TRUST_LEVEL_LABELS' `label`/`description` above as the English fallback
 * -- rather than ever rendering the hardcoded English fields directly.
 */
export function trustLevelLabelKey(level: TrustLevel): string {
  return `trust_ladder.${level}_label`;
}

export function trustLevelDescriptionKey(level: TrustLevel): string {
  return `trust_ladder.${level}_description`;
}

/** @deprecated */
export const TRUST_LADDER_LABELS = TRUST_LEVEL_LABELS;

export function inferClaimType(record: PublicRecord): ClaimType {
  if (record.claimType) return record.claimType;
  if (record.recordType === 'individual_memory') return 'personal_memory';
  if (record.recordType === 'individual_legacy') return 'community_story';
  if (record.recordType === 'heritage_archive' || record.recordType === 'cultural_collection') {
    return 'heritage';
  }
  if (record.recordType === 'institution_verified_record' && record.pillar === 'Hero')
    return 'contribution';
  if (record.recordType === 'institution_certificate') return 'institutional_recognition';
  if (record.recordType === 'individual_achievement') return 'achievement';
  return 'other';
}

export function recordRequiresVerification(record: PublicRecord): boolean {
  if (NO_VERIFICATION_REQUIRED_TYPES.has(record.recordType)) return false;
  const claim = inferClaimType(record);
  return HIGH_RISK_CLAIM_TYPES.has(claim) || CERTIFICATE_ELIGIBLE_TYPES.has(record.recordType);
}

export function isHighRiskClaim(record: PublicRecord): boolean {
  return HIGH_RISK_CLAIM_TYPES.has(inferClaimType(record));
}

export function isPubliclyShareable(record: PublicRecord): boolean {
  return PUBLIC_VISIBILITIES.has(record.visibility) && !record.isArchived;
}

export function isCommunitySummaryVisible(record: PublicRecord): boolean {
  return (
    isPubliclyShareable(record) &&
    record.communitySummaryEnabled !== false &&
    record.visibility !== 'private'
  );
}

export function hasStructuredMetadata(record: PublicRecord): boolean {
  return (
    record.title.trim().length > 0 &&
    record.description.trim().length > 8 &&
    record.locationName.trim().length > 0 &&
    Boolean(record.date)
  );
}

export function hasEvidence(record: PublicRecord): boolean {
  return record.evidenceCount >= 1 || record.evidenceStatus === 'added';
}

export function hasWitnessConfirmation(record: PublicRecord): boolean {
  return (
    WITNESS_OR_HIGHER.includes(record.verificationStatus) || record.witnessStatus === 'confirmed'
  );
}

export function hasInstitutionReview(record: PublicRecord): boolean {
  return (
    INSTITUTION_REVIEWED.includes(record.verificationStatus) ||
    record.institutionReviewStatus === 'approved'
  );
}

export function isCertificateTypeEligible(record: PublicRecord): boolean {
  if (NO_VERIFICATION_REQUIRED_TYPES.has(record.recordType)) return false;
  return CERTIFICATE_ELIGIBLE_TYPES.has(record.recordType);
}

export function ownerAllowsCertificate(record: PublicRecord): boolean {
  if (record.allowCertificateIssuance === false) return false;
  if (NO_VERIFICATION_REQUIRED_TYPES.has(record.recordType)) return false;
  return true;
}

export function isCertificateEligible(record: PublicRecord): boolean {
  if (record.certificateEligible === true) return ownerAllowsCertificate(record);
  if (!ownerAllowsCertificate(record)) return false;
  if (!hasStructuredMetadata(record)) return false;
  if (!hasEvidence(record) || record.evidenceCount < 2) return false;
  if (!hasWitnessConfirmation(record) && !hasInstitutionReview(record)) return false;
  if (!isCertificateTypeEligible(record)) return false;
  if (isHighRiskClaim(record) && !hasInstitutionReview(record)) return false;
  return true;
}

export function isCertificateIssued(record: PublicRecord): boolean {
  return Boolean(record.certificateIssued || record.certificateId) && isCertificateEligible(record);
}

export function canAccessCertificate(
  record: PublicRecord,
  options?: { viewerAuthorized?: boolean }
): boolean {
  if (!isCertificateIssued(record)) return false;
  if (options?.viewerAuthorized) return true;
  return Boolean(record.certificateShareEnabled);
}

export function isProofSummaryEligible(record: PublicRecord): boolean {
  if (record.isArchived || record.verificationStatus === 'Archived') return false;
  if (record.visibility === 'private' && !record.externalSharingEnabled) return false;
  if (!hasStructuredMetadata(record)) return false;
  return true;
}

/** @deprecated */
export function isProofCardEligible(record: PublicRecord): boolean {
  return isProofSummaryEligible(record) && isCommunitySummaryVisible(record);
}

/** Public proof summary / proof card — not the same as verified proof or certificate. */
export function hasProofSummary(record: PublicRecord): boolean {
  return (
    Boolean(record.proofCardId) ||
    (isProofSummaryEligible(record) && isCommunitySummaryVisible(record))
  );
}

/** @deprecated Alias */
export function hasProofCard(record: PublicRecord): boolean {
  return hasProofSummary(record);
}

export function isVerifiedProof(record: PublicRecord): boolean {
  if (NO_VERIFICATION_REQUIRED_TYPES.has(record.recordType)) {
    return false;
  }
  if (!hasEvidence(record) || record.evidenceCount < 2) return false;
  if (!hasWitnessConfirmation(record) && !hasInstitutionReview(record)) return false;
  if (!hasStructuredMetadata(record)) return false;
  if (isHighRiskClaim(record) && !hasInstitutionReview(record)) return false;
  return true;
}

function computeBaseTrustLevel(record: PublicRecord): TrustLevel {
  if (record.isArchived || record.verificationStatus === 'Archived') return 'archived';
  if (isCertificateIssued(record)) return 'certificate_issued';
  if (isCertificateEligible(record) || record.certificateEligible) return 'certificate_eligible';
  if (isVerifiedProof(record)) return 'verified_proof';
  if (hasInstitutionReview(record)) return 'institution_reviewed';
  if (hasWitnessConfirmation(record) || record.verificationStatus === 'Peer Reviewed') {
    return 'witness_confirmed';
  }
  if (hasEvidence(record)) return 'evidence_added';
  if (record.verificationStatus === 'Draft' || record.verificationStatus === 'Submitted') {
    return 'draft';
  }
  return 'self_recorded';
}

/** Cap trust for personal stories — never show as verified proof without review. */
function capTrustForRecordType(record: PublicRecord, level: TrustLevel): TrustLevel {
  if (NO_VERIFICATION_REQUIRED_TYPES.has(record.recordType)) {
    const order: TrustLevel[] = ['draft', 'self_recorded', 'evidence_added', 'witness_confirmed'];
    const maxIdx = order.indexOf('witness_confirmed');
    const idx = order.indexOf(level);
    if (idx < 0) return 'witness_confirmed';
    if (idx > maxIdx) return 'witness_confirmed';
    return level;
  }
  if (isHighRiskClaim(record) && !hasInstitutionReview(record)) {
    const capped: TrustLevel[] = [
      'draft',
      'self_recorded',
      'evidence_added',
      'witness_confirmed',
      'institution_reviewed',
    ];
    if (!capped.includes(level)) return 'institution_reviewed';
  }
  return level;
}

export function getTrustLevel(record: PublicRecord): TrustLevel {
  const base = record.trustLevel ?? computeBaseTrustLevel(record);
  return capTrustForRecordType(record, base);
}

/** @deprecated */
export function getTrustLadderStatus(record: PublicRecord): TrustLevel {
  return getTrustLevel(record);
}

export function getRecordDisplayLabel(record: PublicRecord): string {
  const level = getTrustLevel(record);
  if (level === 'archived') return 'Archived';
  if (NO_VERIFICATION_REQUIRED_TYPES.has(record.recordType)) {
    if (record.visibility === 'private') return 'Private Record';
    if (level === 'self_recorded') return 'Community Story';
    return 'Personal Record';
  }
  if (level === 'self_recorded') return 'Self-Recorded';
  if (level === 'verified_proof') return 'Verified Proof';
  if (hasProofSummary(record)) return 'Public Record Summary';
  return TRUST_LEVEL_LABELS[level].label;
}

export type ReviewTrailStep = {
  id: string;
  label: string;
  completed: boolean;
  detail?: string;
};

export function buildReviewTrail(record: PublicRecord): ReviewTrailStep[] {
  const steps: ReviewTrailStep[] = [
    {
      id: 'created',
      label: 'Record created',
      completed: Boolean(record.date),
      detail: record.date ? new Date(record.date).toLocaleDateString() : undefined,
    },
    {
      id: 'metadata',
      label: 'Metadata completed',
      completed: hasStructuredMetadata(record),
    },
    {
      id: 'evidence',
      label: 'Evidence added',
      completed: hasEvidence(record),
      detail: hasEvidence(record) ? `${record.evidenceCount} item(s)` : undefined,
    },
    {
      id: 'witness',
      label: 'Witness confirmation received',
      completed: hasWitnessConfirmation(record),
    },
    {
      id: 'institution',
      label: 'Institution review completed',
      completed: hasInstitutionReview(record),
    },
    {
      id: 'proof_summary',
      label: 'Proof summary generated',
      completed: hasProofSummary(record),
    },
    {
      id: 'certificate',
      label: 'Certificate issued',
      completed: isCertificateIssued(record),
      detail:
        isCertificateIssued(record) && !record.certificateShareEnabled ? 'Private' : undefined,
    },
  ];
  return steps;
}

export function getProofCardId(record: PublicRecord): string {
  return record.proofCardId ?? `EPP-${record.id.replace(/^rec-/, '').toUpperCase()}`;
}

export function getCertificateId(record: PublicRecord): string | undefined {
  if (!isCertificateIssued(record)) return undefined;
  return record.certificateId;
}

export type ProofDocumentKind = 'proof_summary' | 'certificate';

export function getPublicDocumentKind(record: PublicRecord): ProofDocumentKind {
  return isCertificateIssued(record) ? 'certificate' : 'proof_summary';
}

export function getProofSummaryTitle(record: PublicRecord): string {
  if (isVerifiedProof(record)) return 'Verified Record Summary';
  if (record.recordType === 'heritage_archive' || record.recordType === 'cultural_collection') {
    return 'Curated Archive Proof Card';
  }
  if (NO_VERIFICATION_REQUIRED_TYPES.has(record.recordType)) {
    return 'Public Record Summary';
  }
  return 'Public Proof Summary';
}

/** @deprecated */
export function getProofCardTitle(record: PublicRecord): string {
  return getProofSummaryTitle(record);
}

export function getCertificateTitle(record: PublicRecord): string {
  if (record.recordType === 'heritage_archive' || record.recordType === 'cultural_collection') {
    return 'Certificate of Heritage Contribution';
  }
  if (record.recordType === 'institution_verified_record' && record.pillar === 'Hero') {
    return 'Certificate of Contribution';
  }
  if (record.recordType === 'institution_certificate') {
    return 'Certificate of Institution-Reviewed Record';
  }
  return 'Certificate of Verified Achievement';
}

export const PROOF_SUMMARY_DISCLAIMER =
  'This proof summary preserves structured evidence and review context associated with a record on Ertzyx. It complements, and does not replace, original credentials or official institutional records.';

/** @deprecated */
export const PROOF_CARD_DISCLAIMER = PROOF_SUMMARY_DISCLAIMER;

export const CERTIFICATE_DISCLAIMER =
  'This certificate does not replace credentials issued by schools, employers, governments, or official authorities. It preserves verification context around a record created or reviewed on Ertzyx.';

export const PROOF_SUMMARY_INTRO =
  'A limited public view of a preserved record’s context, evidence status, and review level.';

/** @deprecated */
export const PROOF_CARD_INTRO = PROOF_SUMMARY_INTRO;

export const CERTIFICATE_INTRO =
  'Issued after evidence review and verification context recorded on Ertzyx.';

export const TRUST_INTENT_HELPER =
  'Not every record needs verification. Personal memories can be preserved as self-recorded. Verification is used when you want stronger public trust.';

export const NO_PROOF_NOTE =
  'Self-recorded stories are preserved but not verified. You can add evidence, witnesses, or institution review later.';

export function shouldShowCertificateInPublicListings(record: PublicRecord): boolean {
  return false;
}

export function getPublicSummaryFields(record: PublicRecord) {
  return {
    title: record.title,
    ownerName: record.ownerName,
    locationName: record.locationName,
    evidenceCount: record.evidenceCount,
    recordId: getProofCardId(record),
    trustLabel: getRecordDisplayLabel(record),
    reviewStatus: TRUST_LEVEL_LABELS[getTrustLevel(record)].label,
  };
}
